import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { kg, money } from "@/lib/calc";
import { ChevronLeft, ChevronRight, Trophy } from "lucide-react";

function Sparkline({ data, color = "#0d9488" }) {
  if (!data?.length) return <div className="w-20 h-6" />;
  const max = Math.max(...data, 1), min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / Math.max(1, data.length - 1)) * 80},${24 - ((v - min) / range) * 20 - 2}`).join(" ");
  return <svg width="80" height="24" className="overflow-visible"><polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export default function Workers() {
  const { canSeeMoney } = useAuth();
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const load = (wk) => api.get("/workers/stats", { params: wk != null ? { week: wk } : {} }).then((r) => { setData(r.data); setWeek(r.data.week); });
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-6 text-slate-800">Loading…</div>;

  const rankBadge = (r) => r === 1 ? "bg-amber text-slate-800" : r === 2 ? "bg-teal-100 text-teal-800" : r === 3 ? "bg-leaf text-slate-800" : "bg-slate-100 text-slate-500";

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800">Workers</h1><p className="text-slate-500 text-sm">Weekly performance</p></div>
        <div className="flex items-center gap-1 bg-white rounded-md border border-border">
          <button data-testid="w-week-prev" onClick={() => load(week - 1)} className="p-2.5"><ChevronLeft size={18} strokeWidth={2.5} /></button>
          <span className="font-num font-semibold text-slate-800 px-1 text-sm">Week {week}</span>
          <button data-testid="w-week-next" onClick={() => load(week + 1)} className="p-2.5"><ChevronRight size={18} strokeWidth={2.5} /></button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="btn-grad text-white text-left">
              <th className="sticky left-0 btn-grad px-4 py-3 font-semibold">Worker</th>
              <th className="px-3 py-3 font-semibold text-right">Minutes</th>
              <th className="px-3 py-3 font-semibold text-right">Buckets</th>
              <th className="px-3 py-3 font-semibold text-right">Weighed kg</th>
              <th className="px-3 py-3 font-semibold text-right">Earned kg</th>
              <th className="px-3 py-3 font-semibold">Trend</th>
              {canSeeMoney && <th className="px-3 py-3 font-semibold text-right">Piece $</th>}
            </tr>
          </thead>
          <tbody>
            {data.workers.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-10">No worker activity this week.</td></tr>}
            {data.workers.map((w) => (
              <tr key={w.worker_id} data-testid={`worker-row-${w.worker_id}`} className="border-t border-slate-100">
                <td className="sticky left-0 bg-white px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                  <span className={`inline-grid place-items-center w-6 h-6 rounded-full text-xs font-bold mr-2 ${rankBadge(w.rank)}`}>{w.rank}</span>
                  {w.number != null && <span className="font-num text-teal-600 font-semibold mr-1.5">#{w.number}</span>}
                  {w.name}
                  {w.code && <span className="text-slate-400 text-xs font-num ml-2">{w.code}</span>}
                </td>
                <td className="px-3 py-3 text-right font-num">{w.minutes}</td>
                <td className="px-3 py-3 text-right font-num">{w.buckets}</td>
                <td className="px-3 py-3 text-right font-num">{kg(w.weighed_kg)}</td>
                <td className="px-3 py-3 text-right font-num font-semibold text-slate-800">{kg(w.earned_kg)}</td>
                <td className="px-3 py-3"><Sparkline data={w.trend} /></td>
                {canSeeMoney && <td className="px-3 py-3 text-right font-num">{money(w.piece_dollars || 0)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
